import { useState } from 'react';
import { Permissions } from '@/types/auth';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Eye, PencilSimple, Trash, Plus, ShieldCheck } from '@phosphor-icons/react';
import { ScrollArea } from './ui/scroll-area';

interface PermissionsEditorProps {
  permissions: Permissions;
  onSave: (permissions: Permissions) => void;
  onCancel: () => void;
}

export const PermissionsEditor = ({ permissions, onSave, onCancel }: PermissionsEditorProps) => {
  const [editedPermissions, setEditedPermissions] = useState<Permissions>({ ...permissions });

  const handleToggle = (key: keyof Permissions) => {
    setEditedPermissions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editedPermissions);
  };

  const viewPermissions: Array<{ key: keyof Permissions; label: string; description: string }> = [
    { key: 'viewDashboard', label: 'Dashboard', description: 'View main dashboard with overview stats' },
    { key: 'viewLeaderboard', label: 'Leaderboard', description: 'View boxer rankings and leaderboard' },
    { key: 'viewUpcomingFights', label: 'Upcoming Fights', description: 'View scheduled upcoming fights' },
    { key: 'viewBoxerProfiles', label: 'Boxer Profiles', description: 'View detailed boxer profiles' },
    { key: 'viewBoxerDirectory', label: 'Boxer Directory', description: 'Browse all boxers and sponsors' },
    { key: 'viewFightCards', label: 'Fight Cards', description: 'View fight card details' },
    { key: 'viewSponsors', label: 'Sponsors', description: 'View sponsor information' },
    { key: 'viewLicenses', label: 'Licenses', description: 'View license management section' },
    { key: 'viewBetting', label: 'Betting', description: 'View betting interface and pools' },
    { key: 'viewTournaments', label: 'Tournaments', description: 'View tournament brackets' },
    { key: 'viewRankings', label: 'Rankings', description: 'View ranking system information' },
    { key: 'viewSettings', label: 'Settings', description: 'Access settings panel' },
    { key: 'viewHelp', label: 'Help', description: 'Access help documentation' },
  ];

  const actionPermissions: Array<{ key: keyof Permissions; label: string; description: string }> = [
    { key: 'registerBoxers', label: 'Register Boxers', description: 'Add new boxers to the system' },
    { key: 'editBoxers', label: 'Edit Boxers', description: 'Modify existing boxer information' },
    { key: 'deleteBoxers', label: 'Delete Boxers', description: 'Remove boxers from the system' },
    { key: 'generateFightCards', label: 'Generate Fight Cards', description: 'Create new fight cards' },
    { key: 'editFightCards', label: 'Edit Fight Cards', description: 'Modify existing fight cards' },
    { key: 'declareFightResults', label: 'Declare Results', description: 'Enter fight results and outcomes' },
    { key: 'registerSponsors', label: 'Register Sponsors', description: 'Add new sponsors' },
    { key: 'editSponsors', label: 'Edit Sponsors', description: 'Modify sponsor information' },
    { key: 'deleteSponsors', label: 'Delete Sponsors', description: 'Remove sponsors' },
    { key: 'manageLicenses', label: 'Manage Licenses', description: 'Process license payments and renewals' },
    { key: 'manageBetting', label: 'Manage Betting', description: 'Create and manage betting pools' },
    { key: 'settleBets', label: 'Settle Bets', description: 'Process bet settlements and payouts' },
    { key: 'manageTournaments', label: 'Manage Tournaments', description: 'Create and manage tournaments' },
    { key: 'editRankingSettings', label: 'Edit Rankings', description: 'Modify ranking system settings' },
    { key: 'editAppSettings', label: 'Edit App Settings', description: 'Change application configuration' },
    { key: 'resetSeason', label: 'Reset Season', description: 'Perform season reset operations' },
    { key: 'importExportData', label: 'Import/Export', description: 'Import and export system data' },
    { key: 'manageUsers', label: 'Manage Users', description: 'Create, edit, and delete user accounts' },
  ];

  const enableAll = () => {
    const allEnabled = Object.keys(editedPermissions).reduce((acc, key) => {
      acc[key as keyof Permissions] = true;
      return acc;
    }, {} as Permissions);
    setEditedPermissions(allEnabled);
  };

  const disableAll = () => {
    const allDisabled = Object.keys(editedPermissions).reduce((acc, key) => {
      acc[key as keyof Permissions] = false;
      return acc;
    }, {} as Permissions);
    setEditedPermissions(allDisabled);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-base md:text-lg font-semibold">Custom Permissions</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Configure exactly what this user can see and do
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={enableAll} className="flex-1 sm:flex-none">
            <ShieldCheck className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Enable All</span>
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={disableAll} className="flex-1 sm:flex-none">
            <span className="hidden sm:inline">Disable All</span>
            <span className="sm:hidden">Disable</span>
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[400px] md:h-[500px] pr-2 md:pr-4">
        <div className="space-y-4 md:space-y-6">
          {/* View Permissions */}
          <div>
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <Eye className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              <h4 className="text-sm md:text-base font-semibold text-primary">View Permissions</h4>
            </div>
            <div className="space-y-2 md:space-y-3 pl-0 md:pl-7">
              {viewPermissions.map(({ key, label, description }) => (
                <div key={key} className="flex items-start justify-between gap-2 md:gap-4 p-2 md:p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={key} className="text-xs md:text-sm font-medium cursor-pointer">
                      {label}
                    </Label>
                    <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden sm:block">
                      {description}
                    </p>
                  </div>
                  <Switch
                    id={key}
                    checked={editedPermissions[key]}
                    onCheckedChange={() => handleToggle(key)}
                  />
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Action Permissions */}
          <div>
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <PencilSimple className="w-4 h-4 md:w-5 md:h-5 text-secondary" />
              <h4 className="text-sm md:text-base font-semibold text-secondary">Action Permissions</h4>
            </div>
            <div className="space-y-2 md:space-y-3 pl-0 md:pl-7">
              {actionPermissions.map(({ key, label, description }) => (
                <div key={key} className="flex items-start justify-between gap-2 md:gap-4 p-2 md:p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={key} className="text-xs md:text-sm font-medium cursor-pointer">
                      {label}
                    </Label>
                    <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden sm:block">
                      {description}
                    </p>
                  </div>
                  <Switch
                    id={key}
                    checked={editedPermissions[key]}
                    onCheckedChange={() => handleToggle(key)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>

      <div className="flex flex-col sm:flex-row gap-2 justify-end pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button type="submit" className="w-full sm:w-auto">
          Save Permissions
        </Button>
      </div>
    </form>
  );
};
